/**
 * MatrixViewer — read-only view of the rig-wide MIDI matrix.
 *
 * Phase 1 deliverable: shows every device, every slot. No editing.
 * Editing arrives in Phase 2 once the empty-state flow is well understood.
 *
 * Multi-claim cells (e.g. PCR + TOSC2 both transmitting CC 1 ch 13 to
 * trigger Scene 1) render with the primary claim's label + tint, with a
 * small "2×" / "3×" chip in the top-right corner. Hover/title shows the
 * full claim list. No stacked text — grid stays uniform height.
 */

import { createMemo, createSignal, For, onCleanup, onMount, Show } from 'solid-js';
import { Portal } from 'solid-js/web';
import {
  type MatrixDevice,
  type MatrixSlot,
  deviceForClaim,
  primaryClaim,
} from '@opbuttons/schema';
import { attachAutosize } from './matrixCellAutosize.ts';
import {
  matrixLibrary,
  addDevice,
  claimsForDevice,
  removeDevice,
  reserveNoteRow,
  reservePcRow,
  type SlotLocator,
} from '../matrix/store.ts';
import { buildMatrixCsv } from '../matrix/export.ts';
import { barPosition } from '../settings/store.ts';
import { downloadBlob, timestampForFilename } from '../bundle/download.ts';
import { DeviceEditor } from './DeviceEditor.tsx';
import { SlotEditor } from './SlotEditor.tsx';
import { ConfirmChoice } from './ConfirmChoice.tsx';

interface MatrixViewerProps {
  onClose: () => void;
}

const CHANNELS: number[] = Array.from({ length: 16 }, (_, i) => i + 1);

/** Split a device name at its first whitespace so cells can render the
 *  first word on line 1 and everything after on line 2. Two lines makes
 *  long names like "TouchOSC iPad #1" or "Midi Fighter Twister" fit the
 *  100px-wide cell cleanly, with the most identifying token ("TouchOSC",
 *  "Midi") on top where the eye lands first. Single-word names get an
 *  empty rest string; the rendered slot stays present (empty) so all
 *  cells align vertically across the grid. */
function splitDeviceName(name: string): { first: string; rest: string } {
  const trimmed = name.trim();
  const i = trimmed.search(/\s/);
  if (i < 0) return { first: trimmed, rest: '' };
  return { first: trimmed.slice(0, i), rest: trimmed.slice(i + 1).trim() };
}

export function MatrixViewer(props: MatrixViewerProps) {
  // Edit mode unlocks the device legend for add/edit/delete. Stays
  // viewer-local — there's no project-wide "matrix edit mode."
  // Selecting a device id opens its inline editor; null = none open.
  const [editMode, setEditMode] = createSignal(false);
  const [selectedDeviceId, setSelectedDeviceId] = createSignal<string | null>(null);
  // Confirm-delete is its own state so the user sees the orphan-claim
  // count BEFORE the device disappears. Holds the pending device id.
  const [pendingDelete, setPendingDelete] = createSignal<string | null>(null);
  // Cell-click in edit mode opens the SlotEditor for that locator.
  const [editingSlot, setEditingSlot] = createSignal<SlotLocator | null>(null);

  function onKey(e: KeyboardEvent): void {
    if (e.key === 'Escape') {
      // Edit-side state takes precedence on Esc — close the deepest
      // open layer before bubbling to the viewer close.
      if (pendingDelete()) { setPendingDelete(null); return; }
      if (editingSlot()) { setEditingSlot(null); return; }
      if (selectedDeviceId()) { setSelectedDeviceId(null); return; }
      props.onClose();
    }
  }
  onMount(() => window.addEventListener('keydown', onKey));
  onCleanup(() => window.removeEventListener('keydown', onKey));

  const library = matrixLibrary;

  function onAddDevice(): void {
    const d = addDevice();
    setSelectedDeviceId(d.id);
  }

  function onDeviceChipClick(id: string): void {
    setSelectedDeviceId((cur) => (cur === id ? null : id));
  }

  function requestDelete(id: string): void {
    const orphaned = claimsForDevice(id);
    if (orphaned === 0) {
      // No co-owned slots at risk — proceed without prompt.
      removeDevice(id);
      setSelectedDeviceId(null);
      return;
    }
    setPendingDelete(id);
  }

  function confirmDelete(): void {
    const id = pendingDelete();
    if (!id) return;
    removeDevice(id);
    setSelectedDeviceId(null);
    setPendingDelete(null);
  }

  const pendingDeviceInfo = createMemo(() => {
    const id = pendingDelete();
    if (!id) return null;
    const device = library().devices.find((d) => d.id === id);
    if (!device) return null;
    return { device, orphaned: claimsForDevice(id) };
  });

  const selectedDevice = createMemo(() => {
    const id = selectedDeviceId();
    if (!id) return null;
    return library().devices.find((d) => d.id === id) ?? null;
  });

  const ccSlots = createMemo<MatrixSlot[]>(() =>
    library().slots.filter((s) => s.kind === 'cc')
  );
  const noteSlots = createMemo<MatrixSlot[]>(() =>
    library().slots.filter((s) => s.kind === 'note')
  );

  // Full 0..127 CC grid — the matrix IS the artifact, claims paint on top.
  const ccRows = createMemo<number[]>(() =>
    Array.from({ length: 128 }, (_, i) => i)
  );

  // Note rows = labels claimed somewhere, sorted by canonical MIDI # when
  // known. No "full 128 notes" default because note labels are non-canonical
  // (±12 manufacturer offset) and showing all of them is misleading.
  const noteRows = createMemo<string[]>(() => {
    const seen = new Map<string, number | undefined>();
    for (const s of noteSlots()) {
      const label = s.noteLabel;
      if (!label) continue;
      if (!seen.has(label)) seen.set(label, s.noteMidi);
    }
    return Array.from(seen.entries())
      .sort((a, b) => {
        const am = a[1] ?? Number.POSITIVE_INFINITY;
        const bm = b[1] ?? Number.POSITIVE_INFINITY;
        if (am !== bm) return am - bm;
        return a[0].localeCompare(b[0]);
      })
      .map(([label]) => label);
  });

  // Program rows follow the NOTE pattern, not the CC one: a rig uses a
  // handful of programs, so a full 0..127 grid would double the viewer's
  // height to show mostly nothing. Claimed rows only, plus an adder.
  const pcSlots = createMemo<MatrixSlot[]>(() =>
    library().slots.filter((s) => s.kind === 'pc')
  );
  const pcRows = createMemo<number[]>(() => {
    const seen = new Set<number>();
    for (const s of pcSlots()) {
      if (typeof s.pcNumber === 'number') seen.add(s.pcNumber);
    }
    return Array.from(seen).sort((a, b) => a - b);
  });
  function pcSlotAt(pcNumber: number, channel: number): MatrixSlot | undefined {
    return pcSlots().find((s) => s.pcNumber === pcNumber && s.channel === channel);
  }

  function ccSlotAt(ccNumber: number, channel: number): MatrixSlot | undefined {
    return ccSlots().find((s) => s.ccNumber === ccNumber && s.channel === channel);
  }
  function noteSlotAt(label: string, channel: number): MatrixSlot | undefined {
    return noteSlots().find((s) => s.noteLabel === label && s.channel === channel);
  }

  const hasDevices = createMemo(() => library().devices.length > 0);
  const hasNoteClaims = createMemo(() => noteSlots().length > 0);
  const hasPcClaims = createMemo(() => pcSlots().length > 0);

  const totalClaims = createMemo(() =>
    library().slots.reduce((sum, s) => sum + s.claims.length, 0)
  );

  function exportCsv(): void {
    const csv = buildMatrixCsv(library());
    downloadBlob(csv, `opbuttons-matrix-${timestampForFilename()}.csv`, 'text/csv');
  }

  // Portal out of .shell so the modal uses the real browser viewport
  // width (100vw), not the shell's scaled iPad-frame width. Grid can
  // then expand horizontally when the user widens the browser window.
  return (
    <Portal>
      <div
        class="matrix-overlay"
        onClick={props.onClose}
        role="dialog"
        aria-modal="true"
        aria-label="MIDI matrix"
      >
        <div class="matrix-modal" data-bar-position={barPosition()} onClick={(e) => e.stopPropagation()}>
        <header class="matrix-head">
          <div class="matrix-title">MIDI MATRIX</div>
          <div class="matrix-head-actions">
            <button
              type="button"
              class="matrix-edit-toggle"
              data-on={editMode() ? 'true' : 'false'}
              onClick={() => {
                const next = !editMode();
                setEditMode(next);
                if (!next) setSelectedDeviceId(null);
              }}
              title={editMode() ? 'Leave edit mode' : 'Edit devices'}
            >
              {editMode() ? 'Done editing' : 'Edit'}
            </button>
            <button
              type="button"
              class="matrix-close"
              onClick={props.onClose}
              title="Close (Esc)"
              aria-label="Close"
            >
              ×
            </button>
          </div>
        </header>

        <div class="matrix-body">
          <Show when={hasDevices() || editMode()}>
            <section class="matrix-section">
              <div class="matrix-section-head">Devices</div>
              <div class="matrix-legend" data-edit={editMode() ? 'true' : 'false'}>
                <For each={library().devices}>
                  {(d) => (
                    <button
                      type="button"
                      class="matrix-legend-chip"
                      data-hue={d.color}
                      data-selected={selectedDeviceId() === d.id ? 'true' : 'false'}
                      data-clickable={editMode() ? 'true' : 'false'}
                      onClick={() => {
                        if (editMode()) onDeviceChipClick(d.id);
                      }}
                      title={editMode() ? 'Click to edit' : d.notes ?? d.name}
                    >
                      <span class="matrix-legend-swatch" data-hue={d.color} />
                      {d.name}
                    </button>
                  )}
                </For>
                <Show when={editMode()}>
                  <button
                    type="button"
                    class="matrix-legend-add"
                    onClick={onAddDevice}
                    title="Add a new device to the matrix"
                  >
                    + Add device
                  </button>
                </Show>
              </div>
              <Show when={selectedDevice()}>
                {(device) => (
                  <DeviceEditor
                    device={device()}
                    onClose={() => setSelectedDeviceId(null)}
                    onDelete={() => requestDelete(device().id)}
                  />
                )}
              </Show>
            </section>
          </Show>

          <section class="matrix-section">
            <div class="matrix-section-head">
              CC · channels 1–16 × controllers 0–127
            </div>
            <MatrixGrid
              rows={ccRows()}
              rowLabel={(r) => `CC ${r}`}
              cellAt={(r, ch) => ccSlotAt(r as number, ch)}
              library={library()}
              editMode={editMode()}
              onCellClick={(r, ch) => setEditingSlot({
                kind: 'cc', channel: ch, ccNumber: r as number,
              })}
            />
          </section>

          <Show when={hasNoteClaims() || editMode()}>
            <section class="matrix-section">
              <div class="matrix-section-head">Note claims</div>
              <MatrixGrid
                rows={noteRows()}
                rowLabel={(r) => String(r)}
                cellAt={(r, ch) => noteSlotAt(r as string, ch)}
                library={library()}
                editMode={editMode()}
                onCellClick={(r, ch) => setEditingSlot({
                  kind: 'note', channel: ch, noteLabel: r as string,
                })}
              />
              <Show when={editMode()}>
                <NewNoteRowAdder
                  onAdd={(channel, label) => {
                    reserveNoteRow(channel, label);
                    setEditingSlot({ kind: 'note', channel, noteLabel: label });
                  }}
                />
              </Show>
            </section>
          </Show>

          {/* Program Change claims (2026-08-10). Without this section a pc
              slot could exist in the library and in an exported CSV while
              being invisible in the rig map - data you cannot see is data you
              cannot trust. */}
          <Show when={hasPcClaims() || editMode()}>
            <section class="matrix-section">
              <div class="matrix-section-head">Program Change claims</div>
              <MatrixGrid
                rows={pcRows()}
                rowLabel={(r) => `PC ${r}`}
                cellAt={(r, ch) => pcSlotAt(r as number, ch)}
                library={library()}
                editMode={editMode()}
                onCellClick={(r, ch) => setEditingSlot({
                  kind: 'pc', channel: ch, pcNumber: r as number,
                })}
              />
              <Show when={editMode()}>
                <NewPcRowAdder
                  onAdd={(channel, pcNumber) => {
                    reservePcRow(channel, pcNumber);
                    setEditingSlot({ kind: 'pc', channel, pcNumber });
                  }}
                />
              </Show>
            </section>
          </Show>
        </div>

        <Show when={editingSlot()}>
          {(loc) => (
            <SlotEditor
              locator={loc()}
              onClose={() => setEditingSlot(null)}
            />
          )}
        </Show>

        <Show when={pendingDeviceInfo()}>
          {(info) => (
            <ConfirmChoice
              title="Delete device?"
              message={
                `"${info().device.name}" has ${info().orphaned} claim`
                + `${info().orphaned === 1 ? '' : 's'} on the matrix.`
                + ' Deleting the device also removes those claims'
                + ' (and any slots that become empty as a result).'
              }
              options={[{
                key: 'delete',
                label: `Delete ${info().device.name}`,
                tone: 'danger',
              }]}
              onChoose={(k) => { if (k === 'delete') confirmDelete(); }}
              onCancel={() => setPendingDelete(null)}
            />
          )}
        </Show>

        <footer class="matrix-foot">
          <div class="matrix-foot-info">
            <span class="matrix-foot-count">
              {library().devices.length} devices · {library().slots.length} slots · {totalClaims()} claims
            </span>
          </div>
          <div class="matrix-foot-actions">
            <button
              type="button"
              class="matrix-export"
              onClick={exportCsv}
              title="Download the full matrix as a spreadsheet-compatible CSV (kind, channel, CC/note, device, control, sharedWith). Opens in Excel / Numbers / Google Sheets."
              disabled={library().slots.length === 0}
            >
              Export CSV
            </button>
          </div>
        </footer>
        </div>
      </div>
    </Portal>
  );
}

/** Shared grid renderer for CC + note sections. Each cell renders the
 *  primary claim's label + device tint, with a "Nx" chip if more than one
 *  claim shares the slot. Tooltip lists all claimants. In edit mode the
 *  cells are clickable. */
function MatrixGrid<R extends number | string>(props: {
  rows: R[];
  rowLabel: (r: R) => string;
  cellAt: (r: R, channel: number) => MatrixSlot | undefined;
  library: { devices: MatrixDevice[]; slots: MatrixSlot[] };
  editMode?: boolean;
  onCellClick?: (r: R, channel: number) => void;
}) {
  function buildTooltip(slot: MatrixSlot): string {
    const parts = slot.claims.map((c) => {
      const d = deviceForClaim(props.library as any, c);
      return `${d?.name ?? '?'} · ${c.controlLabel}`;
    });
    const chLabel = `ch ${slot.channel}`;
    return `${parts.join('  |  ')}  (${chLabel})`;
  }

  return (
    <div class="matrix-grid" role="table">
      <div class="matrix-grid-head" role="row">
        <div class="matrix-grid-corner" />
        <For each={CHANNELS}>
          {(ch) => (
            <div class="matrix-grid-th" role="columnheader" title={`Channel ${ch}`}>
              {ch}
            </div>
          )}
        </For>
      </div>
      <For each={props.rows}>
        {(r) => (
          <div class="matrix-grid-row" role="row">
            <div class="matrix-grid-rh" role="rowheader">{props.rowLabel(r)}</div>
            <For each={CHANNELS}>
              {(ch) => {
                const slot = props.cellAt(r, ch);
                const claim = slot ? primaryClaim(slot) : undefined;
                const device = claim
                  ? deviceForClaim(props.library as any, claim)
                  : undefined;
                const shareCount = slot?.claims.length ?? 0;
                const clickable = props.editMode === true;
                let cellEl: HTMLDivElement | undefined;
                let dispose: (() => void) | undefined;
                onCleanup(() => dispose?.());
                return (
                  <div
                    class="matrix-grid-cell"
                    ref={(el) => {
                      cellEl = el;
                      // Only claimed cells have text worth measuring.
                      // Attach after the current render commits so
                      // scrollWidth is populated.
                      if (slot && cellEl) {
                        dispose?.();
                        dispose = attachAutosize(cellEl);
                      }
                    }}
                    data-claimed={slot ? 'true' : 'false'}
                    data-shared={shareCount > 1 ? 'true' : 'false'}
                    data-hue={device?.color ?? ''}
                    data-editable={clickable ? 'true' : 'false'}
                    title={slot ? buildTooltip(slot) : (clickable ? `Click to claim · ch ${ch}` : `Free · ch ${ch}`)}
                    role="cell"
                    onClick={clickable ? () => props.onCellClick?.(r, ch) : undefined}
                  >
                    {slot && claim ? (() => {
                      const parts = splitDeviceName(device?.name ?? '?');
                      return (
                        <>
                          <span class="matrix-grid-cell-label">
                            <span class="matrix-grid-cell-label-device matrix-grid-cell-label-device-first">
                              {parts.first}
                            </span>
                            <span class="matrix-grid-cell-label-device matrix-grid-cell-label-device-rest">
                              {parts.rest}
                            </span>
                            <span class="matrix-grid-cell-label-control">{claim.controlLabel}</span>
                          </span>
                          <Show when={shareCount > 1}>
                            <span class="matrix-grid-cell-share">{shareCount}×</span>
                          </Show>
                        </>
                      );
                    })() : null}
                  </div>
                );
              }}
            </For>
          </div>
        )}
      </For>
    </div>
  );
}

/** Inline "reserve a new note row" form, shown below the note grid in
 *  edit mode. Reserves the row in the matrix so the row appears in the
 *  grid; then opens SlotEditor at that cell so the user can add the
 *  first claim. */
/** Twin of NewNoteRowAdder for program rows. Kept as its own component
 *  rather than generalising the note one: the two validate different things
 *  (a free-text label vs a 0-127 integer) and a shared "adder" taking a
 *  validator would be more indirection than the twenty lines it saves. */
function NewPcRowAdder(props: { onAdd: (channel: number, pcNumber: number) => void }) {
  const [channel, setChannel] = createSignal('1');
  const [program, setProgram] = createSignal('');

  function submit(): void {
    const ch = Number.parseInt(channel(), 10);
    const pc = Number.parseInt(program(), 10);
    if (!Number.isInteger(ch) || ch < 1 || ch > 16) return;
    if (!Number.isInteger(pc) || pc < 0 || pc > 127) return;
    props.onAdd(ch, pc);
    setProgram('');
  }

  return (
    <div class="matrix-new-note-row">
      <span class="matrix-new-note-label">Add program row</span>
      <input
        type="number"
        class="matrix-new-note-channel"
        value={channel()}
        min={1}
        max={16}
        onInput={(e) => setChannel(e.currentTarget.value)}
        title="MIDI channel 1-16"
        aria-label="MIDI channel"
      />
      <input
        type="number"
        class="matrix-new-note-input"
        value={program()}
        min={0}
        max={127}
        onInput={(e) => setProgram(e.currentTarget.value)}
        placeholder="0-127"
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        aria-label="Program number"
      />
      <button
        type="button"
        class="matrix-new-note-submit"
        onClick={submit}
        disabled={!program().trim()}
      >
        Add row
      </button>
    </div>
  );
}

function NewNoteRowAdder(props: { onAdd: (channel: number, label: string) => void }) {
  const [channel, setChannel] = createSignal('1');
  const [label, setLabel] = createSignal('');

  function submit(): void {
    const ch = Number.parseInt(channel(), 10);
    const lbl = label().trim();
    if (!Number.isInteger(ch) || ch < 1 || ch > 16) return;
    if (!lbl) return;
    props.onAdd(ch, lbl.toUpperCase());
    setLabel('');
  }

  return (
    <div class="matrix-new-note-row">
      <span class="matrix-new-note-label">Add note row</span>
      <input
        type="number"
        class="matrix-new-note-channel"
        value={channel()}
        min={1}
        max={16}
        onInput={(e) => setChannel(e.currentTarget.value)}
        title="MIDI channel 1–16"
        aria-label="MIDI channel"
      />
      <input
        type="text"
        class="matrix-new-note-input"
        value={label()}
        onInput={(e) => setLabel(e.currentTarget.value)}
        placeholder="e.g. C3, F#4"
        onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        aria-label="Note label"
      />
      <button
        type="button"
        class="matrix-new-note-submit"
        onClick={submit}
        disabled={!label().trim()}
      >
        Add row
      </button>
    </div>
  );
}
